import { Dispatch } from "redux";
import { Product } from "../types/productTypes";
import {
  collection,
  query,
  getDocs,
  getDoc,
  where,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  orderBy,
  limit,
  startAfter,
  getFirestore,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../firebase";
import { Cart } from "../types/cartTypes";

export const FETCH_CART_REQUEST = "FETCH_CART_REQUEST";
export const FETCH_CART_SUCCESS = "FETCH_CART_SUCCESS";
export const FETCH_CART_FAILURE = "FETCH_CART_FAILURE";
export const FETCH_CART_RESET = "FETCH_CART_RESET";
export const CREATE_CART_REQUEST = "CREATE_CART_REQUEST";
export const CREATE_CART_SUCCESS = "CREATE_CART_SUCCESS";
export const CREATE_CART_FAILURE = "CREATE_CART_FAILURE";
export const REMOVE_CART_REQUEST = "REMOVE_CART_REQUEST";
export const REMOVE_CART_SUCCESS = "REMOVE_CART_SUCCESS";
export const REMOVE_CART_FAILURE = "REMOVE_CART_FAILURE";
export const UPDATE_CART_QUANTITY_REQUEST = "UPDATE_CART_QUANTITY_REQUEST";
export const UPDATE_CART_QUANTITY_SUCCESS = "UPDATE_CART_QUANTITY_SUCCESS";
export const UPDATE_CART_QUANTITY_FAILURE = "UPDATE_CART_QUANTITY_FAILURE";

export const PAGE_SIZE = 20;

export const fetchCartRequest = () => ({
  type: FETCH_CART_REQUEST,
});

export const fetchCartSuccess = (cartData: {
  products: Product[];
  carts: Cart[];
  totalRecords?: number;
}) => ({
  type: FETCH_CART_SUCCESS,
  payload: cartData,
});

export const fetchCartFailure = (error: string) => ({
  type: FETCH_CART_FAILURE,
  payload: error,
});

export const fetchCartReset = () => ({
  type: FETCH_CART_RESET,
});

// Helper function to get user's document ID by createdTime (userId)
const getUserDocumentId = async (userId: number): Promise<string | null> => {
  try {
    const q = query(
      collection(db, "users"),
      where("createdTime", "==", userId)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    return querySnapshot.docs[0].id;
  } catch (error) {
    console.error("Error getting user document ID:", error);
    return null;
  }
};

export const fetchCartProducts =
  (
    userId: number,
    prevLastDocument: string = "",
    recordLimit: number = PAGE_SIZE
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(fetchCartRequest());
    try {
      const cartPageData: {
        products: Product[];
        carts: Cart[];
        totalRecords?: number;
      } = {
        products: [],
        carts: [],
        totalRecords: 0,
      };

      // Get the user's actual document ID
      const userDocumentId = await getUserDocumentId(userId);
      if (!userDocumentId) {
        throw new Error("User not found");
      }

      // Fetch cart items from users/{userDocumentId}/cart subcollection ordered by createdTime desc
      const cartRef = collection(db, "users", userDocumentId, "cart");
      let cartQuery = query(cartRef, orderBy("createdTime", "desc"));

      // Get total count (without limit)
      const totalCountSnapshot = await getDocs(cartQuery);
      const totalRecords = totalCountSnapshot.size;

      // Apply pagination
      if (prevLastDocument) {
        const firestore = getFirestore();
        const docRef = doc(firestore, "users", userDocumentId, "cart", prevLastDocument);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          cartQuery = query(cartQuery, startAfter(docSnap));
        }
      }

      if (recordLimit) {
        cartQuery = query(cartQuery, limit(recordLimit));
      }

      const cartSnapshot = await getDocs(cartQuery);

      const carts: Cart[] = cartSnapshot.docs.map((doc) => ({
        documentId: doc.id,
        userId: userId,
        productId: doc.data().productId,
        quantity: doc.data().quantity || 1,
        createdTime: doc.data().createdTime || Date.now(),
      }));

      // Extract unique product IDs
      const productIds = Array.from(
        new Set(carts.map((cart) => cart.productId))
      );

      // Fetch product details for those IDs
      let cartProducts: Product[] = [];
      if (productIds.length > 0) {
        // Firestore 'in' query has a limit of 10 items, so we need to batch if needed
        const batches: number[][] = [];
        for (let i = 0; i < productIds.length; i += 10) {
          batches.push(productIds.slice(i, i + 10));
        }

        const productPromises = batches.map((batch) =>
          getDocs(query(collection(db, "products"), where("createdTime", "in", batch)))
        );

        const productSnapshots = await Promise.all(productPromises);
        cartProducts = productSnapshots.flatMap((snapshot) =>
          snapshot.docs.map((doc) => ({
            ...(doc.data() as Product),
            documentId: doc.id,
          }))
        );
      }

      // Merge cart with product data and include quantity
      const products: Product[] = carts
        .map((cart) => {
          const product = cartProducts.find(
            (cartProduct) => cartProduct.createdTime === cart.productId
          );
          if (product) {
            return { ...product, cartQuantity: cart.quantity } as Product;
          }
          return null;
        })
        .filter((product): product is Product => product !== null);

      cartPageData.products = products;
      cartPageData.carts = carts;
      cartPageData.totalRecords = totalRecords;

      dispatch(fetchCartSuccess(cartPageData));
    } catch (error) {
      console.error("Error fetching cart:", error);
      dispatch(fetchCartFailure("Error fetching cart items."));
    }
  };

export const createCart =
  (userId: number, productId: number, quantity: number = 1) =>
  async (dispatch: Dispatch) => {
    dispatch(fetchCartRequest());
    try {
      // Check if cart item already exists
      const existingCartProduct = await getUniqueCartByIDs(userId, productId);
      
      // Get the user's actual document ID
      const userDocumentId = await getUserDocumentId(userId);
      if (!userDocumentId) {
        throw new Error("User not found");
      }

      // Ensure the user document exists first
      const userDocRef = doc(db, "users", userDocumentId);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        await setDoc(
          userDocRef,
          {
            userId: userId,
            createdTime: Date.now(),
          },
          { merge: true }
        );
      }

      // Check product availability first
      const productQuery = query(
        collection(db, "products"),
        where("createdTime", "==", productId)
      );
      const productSnapshot = await getDocs(productQuery);
      
      if (productSnapshot.empty) {
        throw new Error("Product not found");
      }

      const productData = productSnapshot.docs[0].data() as Product;
      const availableQuantity = productData.productQuantity || 0;

      // If item already exists, update quantity instead
      if (existingCartProduct && existingCartProduct.length > 0) {
        const cartItem = existingCartProduct[0];
        const newQuantity = cartItem.quantity + quantity;
        
        if (newQuantity > availableQuantity) {
          throw new Error(`Only ${availableQuantity} available in stock`);
        }

        const cartDocRef = doc(db, "users", userDocumentId, "cart", cartItem.documentId);
        await updateDoc(cartDocRef, {
          quantity: newQuantity,
        });

        // Refetch cart to update state
        dispatch(fetchCartProducts(userId, "", PAGE_SIZE) as any);
      } else {
        // Check quantity before creating
        if (quantity > availableQuantity) {
          throw new Error(`Only ${availableQuantity} available in stock`);
        }

        // Create new cart item
        const cartRef = collection(db, "users", userDocumentId, "cart");
        await addDoc(cartRef, {
          productId: productId,
          quantity: quantity,
          createdTime: Date.now(),
        });

        // Refetch cart to update state
        dispatch(fetchCartProducts(userId, "", PAGE_SIZE) as any);
      }
    } catch (error: any) {
      console.error("Error creating cart:", error);
      dispatch(fetchCartFailure(error.message || "Error creating cart item."));
    }
  };

export const removeCart =
  (userId: number, productId: number) =>
  async (dispatch: Dispatch) => {
    dispatch(fetchCartRequest());
    try {
      // Get the user's actual document ID
      const userDocumentId = await getUserDocumentId(userId);
      if (!userDocumentId) {
        throw new Error("User not found");
      }

      // Find the cart item
      const cartItems = await getUniqueCartByIDs(userId, productId);
      if (cartItems && cartItems.length > 0) {
        const cartItem = cartItems[0];
        const cartDocRef = doc(db, "users", userDocumentId, "cart", cartItem.documentId);
        await deleteDoc(cartDocRef);
      }
      
      // Refetch cart to update state
      dispatch(fetchCartProducts(userId, "", PAGE_SIZE) as any);
    } catch (error) {
      console.error("Error removing cart:", error);
      dispatch(fetchCartFailure("Error removing cart item."));
    }
  };

export const updateCartQuantity =
  (userId: number, productId: number, newQuantity: number) =>
  async (dispatch: Dispatch) => {
    dispatch(fetchCartRequest());
    try {
      // Get the user's actual document ID
      const userDocumentId = await getUserDocumentId(userId);
      if (!userDocumentId) {
        throw new Error("User not found");
      }

      // Check product availability
      const productQuery = query(
        collection(db, "products"),
        where("createdTime", "==", productId)
      );
      const productSnapshot = await getDocs(productQuery);
      
      if (productSnapshot.empty) {
        throw new Error("Product not found");
      }

      const productData = productSnapshot.docs[0].data() as Product;
      const availableQuantity = productData.productQuantity || 0;

      if (newQuantity > availableQuantity) {
        throw new Error(`Only ${availableQuantity} available in stock`);
      }

      if (newQuantity < 1) {
        throw new Error("Quantity must be at least 1");
      }

      // Find the cart item
      const cartItems = await getUniqueCartByIDs(userId, productId);
      if (cartItems && cartItems.length > 0) {
        const cartItem = cartItems[0];
        const cartDocRef = doc(db, "users", userDocumentId, "cart", cartItem.documentId);
        
        if (newQuantity === 0) {
          await deleteDoc(cartDocRef);
        } else {
          await updateDoc(cartDocRef, {
            quantity: newQuantity,
          });
        }

        // Refetch cart to update state with latest product quantities
        dispatch(fetchCartProducts(userId, "", PAGE_SIZE) as any);
      } else {
        throw new Error("Cart item not found");
      }
    } catch (error: any) {
      console.error("Error updating cart quantity:", error);
      dispatch(fetchCartFailure(error.message || "Error updating cart quantity."));
    }
  };

const getUniqueCartByIDs = async (userId: number, productId: number): Promise<Cart[]> => {
  try {
    const carts: Cart[] = [];

    // Get the user's actual document ID
    const userDocumentId = await getUserDocumentId(userId);
    if (!userDocumentId) {
      return carts; // Return empty array if user not found
    }

    // Query users/{userDocumentId}/cart subcollection
    const cartRef = collection(db, "users", userDocumentId, "cart");
    const q = query(cartRef, where("productId", "==", productId));
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((doc) => {
      carts.push({
        documentId: doc.id,
        userId: userId,
        productId: doc.data().productId,
        quantity: doc.data().quantity || 1,
        createdTime: doc.data().createdTime || Date.now(),
      });
    });

    return carts;
  } catch (error) {
    console.error("Error checking cart:", error);
    throw new Error("Error fetching cart item.");
  }
};

// Real-time listener for cart updates
export const setupCartListener =
  (userId: number, dispatch: Dispatch) => {
    return new Promise<void>((resolve, reject) => {
      getUserDocumentId(userId)
        .then((userDocumentId) => {
          if (!userDocumentId) {
            reject(new Error("User not found"));
            return;
          }

          const cartRef = collection(db, "users", userDocumentId, "cart");
          const cartQuery = query(cartRef, orderBy("createdTime", "desc"));

          const unsubscribe = onSnapshot(
            cartQuery,
            async (snapshot) => {
              try {
                const carts: Cart[] = snapshot.docs.map((doc) => ({
                  documentId: doc.id,
                  userId: userId,
                  productId: doc.data().productId,
                  quantity: doc.data().quantity || 1,
                  createdTime: doc.data().createdTime || Date.now(),
                }));

                // Fetch product details
                const productIds = Array.from(
                  new Set(carts.map((cart) => cart.productId))
                );

                let cartProducts: Product[] = [];
                if (productIds.length > 0) {
                  const batches: number[][] = [];
                  for (let i = 0; i < productIds.length; i += 10) {
                    batches.push(productIds.slice(i, i + 10));
                  }

                  const productPromises = batches.map((batch) =>
                    getDocs(query(collection(db, "products"), where("createdTime", "in", batch)))
                  );

                  const productSnapshots = await Promise.all(productPromises);
                  cartProducts = productSnapshots.flatMap((snapshot) =>
                    snapshot.docs.map((doc) => ({
                      ...(doc.data() as Product),
                      documentId: doc.id,
                    }))
                  );
                }

                const products: Product[] = carts
                  .map((cart) => {
                    const product = cartProducts.find(
                      (cartProduct) => cartProduct.createdTime === cart.productId
                    );
                    if (product) {
                      return { ...product, cartQuantity: cart.quantity } as Product;
                    }
                    return null;
                  })
                  .filter((product): product is Product => product !== null);

                dispatch(fetchCartSuccess({
                  products,
                  carts,
                  totalRecords: carts.length,
                }));
              } catch (error) {
                console.error("Error in cart listener:", error);
              }
            },
            (error) => {
              console.error("Error setting up cart listener:", error);
              reject(error);
            }
          );

          resolve();
          return unsubscribe;
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

